import { Pencil, Plus, Save, Trash2, X } from "lucide-react";
import { type FC, type ReactNode, useRef } from "react";

import { m } from "@recobra/i18n/messages";
import { Button } from "@recobra/ui/button";
import { DictationButton } from "@recobra/ui/custom/dictation-button";
import { ExpandableText } from "@recobra/ui/custom/expandable-text";
import { InputGroup, InputGroupAddon, InputGroupTextarea } from "@recobra/ui/input-group";

import { UserLabel } from "./UserLabel";

export const NotesHeader: FC<{
  title: string;
  composerOpen: boolean;
  onOpenComposer: () => void;
  onCloseComposer: () => void;
}> = ({ title, composerOpen, onOpenComposer, onCloseComposer }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
      {title}
    </div>
    {composerOpen ? (
      <Button type="button" variant="ghost" size="sm" onClick={onCloseComposer}>
        <X data-icon="inline-start" />
        {m.case_cancel()}
      </Button>
    ) : (
      <Button type="button" variant="outline" size="sm" onClick={onOpenComposer}>
        <Plus data-icon="inline-start" />
        {m.notes_add()}
      </Button>
    )}
  </div>
);

export const NoteComposer: FC<{
  value: string;
  pending?: boolean;
  onChange: (value: string) => void;
  onAdd: () => void;
}> = ({ value, pending = false, onChange, onAdd }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <InputGroup>
      <InputGroupTextarea
        ref={textareaRef}
        placeholder={m.notes_placeholder()}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
      />
      <InputGroupAddon align="block-end">
        <DictationButton targetRef={textareaRef} value={value} onChange={onChange} />
        <Button
          type="button"
          size="sm"
          className="ml-auto"
          disabled={!value.trim() || pending}
          onClick={onAdd}
        >
          <Plus data-icon="inline-start" />
          {m.notes_add()}
        </Button>
      </InputGroupAddon>
    </InputGroup>
  );
};

export const NotesTimeline: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <div className="relative mt-2">
    <div className="absolute top-2 bottom-4 left-2 w-px -translate-x-1/2 bg-border/80" />
    <div className="relative">{children}</div>
  </div>
);

export const NoteTimelineItem: FC<{
  authorId: string;
  createdAt?: string;
  isLast: boolean;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (value: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onStartEdit: () => void;
  onDelete: () => void;
  savePending?: boolean;
  readOnly?: boolean;
  children: string;
}> = ({
  authorId,
  createdAt,
  isLast,
  isEditing,
  editText,
  onEditTextChange,
  onCancelEdit,
  onSaveEdit,
  onStartEdit,
  onDelete,
  savePending = false,
  readOnly = false,
  children,
}) => (
  <div className="grid grid-cols-[1rem_minmax(0,1fr)] gap-x-5">
    <div className="relative flex justify-center">
      <div className="relative mt-0.5 flex size-4 items-center justify-center rounded-full bg-card">
        <div className="size-2.5 rounded-full bg-foreground" />
      </div>
    </div>
    <div className={isLast ? "pb-1" : "pb-10"}>
      {isEditing ? (
        <NoteEditForm
          value={editText}
          pending={savePending}
          onChange={onEditTextChange}
          onCancel={onCancelEdit}
          onSave={onSaveEdit}
        />
      ) : (
        <div className="group/note">
          <div className="mb-2 flex min-w-0 items-center justify-between gap-3">
            {createdAt ? (
              <span className="text-xs font-semibold text-muted-foreground">{createdAt}</span>
            ) : (
              <span />
            )}
            {!readOnly && (
              <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover/note:opacity-100 group-focus-within/note:opacity-100">
                <Button type="button" variant="ghost" size="icon-xs" onClick={onStartEdit}>
                  <Pencil />
                </Button>
                <Button type="button" variant="destructive" size="icon-xs" onClick={onDelete}>
                  <Trash2 />
                </Button>
              </div>
            )}
          </div>
          <ExpandableText text={children} />
          <UserLabel userId={authorId} className="mt-4" />
        </div>
      )}
    </div>
  </div>
);

const NoteEditForm: FC<{
  value: string;
  pending: boolean;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}> = ({ value, pending, onChange, onCancel, onSave }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <InputGroup>
      <InputGroupTextarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
      />
      <InputGroupAddon align="block-end">
        <DictationButton targetRef={textareaRef} value={value} onChange={onChange} />
        <Button type="button" variant="ghost" size="xs" className="ml-auto" onClick={onCancel}>
          <X data-icon="inline-start" />
          {m.case_cancel()}
        </Button>
        <Button type="button" size="xs" disabled={!value.trim() || pending} onClick={onSave}>
          <Save data-icon="inline-start" />
          {m.clients_save()}
        </Button>
      </InputGroupAddon>
    </InputGroup>
  );
};
